import { useState, useEffect, useMemo } from 'react';
import { Plus, Info, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  useCurrencyConfigurations,
  useAddCurrencyConfiguration,
  useDeleteCurrencyConfiguration,
  useSetPrimaryCurrency
} from '@/hooks/billing';
import { toast } from 'sonner';

interface Currency {
  code: string;
  symbol: string;
  name: string;
}

interface CurrencyConfigurationProps {
  productId: string;
  activeCurrencies?: string[];
  defaultCurrency?: string;
  onCurrenciesChange?: (_currencies: string[]) => void;
  onDefaultChange?: (_currency: string) => void;
}

// Mock currency data
const AVAILABLE_CURRENCIES: Currency[] = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
  { code: 'MXN', symbol: '$', name: 'Mexican Peso' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
];

export function CurrencyConfiguration({ 
  productId, 
  activeCurrencies: propCurrencies,
  defaultCurrency: propDefault,
  onCurrenciesChange,
  onDefaultChange
}: CurrencyConfigurationProps) {
  const { t } = useTranslation();
  
  // API hooks
  const {
    data: currencyConfigs = [],
    isLoading,
    error
  } = useCurrencyConfigurations(productId);
  
  const addCurrencyMutation = useAddCurrencyConfiguration();
  const deleteCurrencyMutation = useDeleteCurrencyConfiguration();
  const setPrimaryMutation = useSetPrimaryCurrency();
  
  // Local state
  const [showAddCurrency, setShowAddCurrency] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Derive active currencies and primary currency from configurations
  console.log('CurrencyConfiguration - currencyConfigs:', currencyConfigs);
  const activeCurrencies = useMemo(
    () =>
      currencyConfigs
        .filter(config => config.isActive)
        .map(config => config.currencyCode),
    [currencyConfigs]
  );
  
  console.log('CurrencyConfiguration - activeCurrencies:', activeCurrencies);
  
  const primaryCurrency = useMemo(
    () =>
      currencyConfigs.find(config => config.isPrimary || config.isDefault)?.currencyCode || 'USD',
    [currencyConfigs]
  );
  
  console.log('CurrencyConfiguration - primaryCurrency:', primaryCurrency);
  
  // Use prop values or API values (check for length to avoid empty array override)
  const currentCurrencies = (propCurrencies && propCurrencies.length > 0) ? propCurrencies : activeCurrencies;
  const currentDefault = propDefault || primaryCurrency;
  
  console.log('CurrencyConfiguration - currentCurrencies:', currentCurrencies);
  console.log('CurrencyConfiguration - currentDefault:', currentDefault);
  
  // Only notify parent when data is initially loaded, not on every change
  // This prevents infinite loops caused by non-memoized callbacks
  useEffect(() => {
    // Only call these once when data is first loaded from API
    // Check if propCurrencies is empty or undefined (not just falsy)
    if (!isLoading && activeCurrencies.length > 0 && (!propCurrencies || propCurrencies.length === 0)) {
      if (onCurrenciesChange) {
        onCurrenciesChange(activeCurrencies);
      }
      if (onDefaultChange && primaryCurrency) {
        onDefaultChange(primaryCurrency);
      }
    }
     
  }, [isLoading, activeCurrencies, onCurrenciesChange, onDefaultChange, primaryCurrency, propCurrencies]);
  
  // Handle API errors
  useEffect(() => {
    if (error) {
      toast.error(t('errors.failedToLoadCurrencyConfiguration'));
    }
  }, [error, t]);


  const handleAddCurrency = async (currencyCode: string) => {
    if (!currentCurrencies.includes(currencyCode)) {
      // Get currency details from available currencies
      const currency = AVAILABLE_CURRENCIES.find(c => c.code === currencyCode);
      if (!currency) return;
      
      // Save to API
      try {
        await addCurrencyMutation.mutateAsync({
          productId,
          data: {
            currencyCode: currency.code,
            symbol: currency.symbol,
            exchangeRate: 1.0, // Default exchange rate, can be updated later
            isPrimary: currentCurrencies.length === 0, // First currency becomes primary
            isActive: true,
            symbolPosition: 'before',
            decimalPlaces: 2,
            thousandSeparator: ',',
            decimalSeparator: '.'
          }
        });
        
        // Update parent state after successful add
        if (onCurrenciesChange) {
          const newCurrencies = [...currentCurrencies, currencyCode];
          onCurrenciesChange(newCurrencies);
        }
        
        // If this is the first currency, set it as default
        if (currentCurrencies.length === 0 && onDefaultChange) {
          onDefaultChange(currencyCode);
        }
        
        toast.success(t('products.currencyAdded'));
      } catch (_error) {
        toast.error(t('errors.failedToUpdateCurrencyConfiguration'));
      }
    }
    setShowAddCurrency(false);
    setSearchQuery('');
  };

  const handleRemoveCurrency = async (currencyCode: string) => {
    if (currencyCode === currentDefault) {
      toast.error(t('errors.cannotRemoveDefaultCurrency'));
      return;
    }
    
    try {
      await deleteCurrencyMutation.mutateAsync({
        productId,
        currencyCode
      });
      
      // Update parent state after successful removal
      if (onCurrenciesChange) {
        const newCurrencies = currentCurrencies.filter(c => c !== currencyCode);
        onCurrenciesChange(newCurrencies);
      }
      
      toast.success(t('products.currencyRemoved'));
    } catch (_error) {
      toast.error(t('errors.failedToUpdateCurrencyConfiguration'));
    }
  };

  const handleSetDefault = async (currencyCode: string) => {
    try {
      await setPrimaryMutation.mutateAsync({
        productId,
        currencyCode
      });
      
      // Update parent state after successful change
      if (onDefaultChange) {
        onDefaultChange(currencyCode);
      }
      
      toast.success(t('products.defaultCurrencyUpdated'));
    } catch (_error) {
      toast.error(t('errors.failedToUpdateDefaultCurrency'));
    }
  };

  const getActiveCurrencyDetails = (code: string): Currency | undefined => {
    return AVAILABLE_CURRENCIES.find(c => c.code === code);
  };

  const filteredCurrencies = AVAILABLE_CURRENCIES.filter(currency => 
    !currentCurrencies.includes(currency.code) &&
    (currency.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
     currency.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  
  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-32" />
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('products.currencyConfiguration')}</CardTitle>
          <CardDescription>
            {t('products.manageCurrenciesDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status and loading indicators */}
          {(addCurrencyMutation.isPending || deleteCurrencyMutation.isPending || setPrimaryMutation.isPending) && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>
                {t('products.updatingCurrencyConfiguration')}
              </AlertDescription>
            </Alert>
          )}
          
          {error && (
            <Alert variant="destructive">
              <AlertDescription>
                {t('errors.failedToLoadCurrencyConfiguration')}
              </AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-3">
            <Label className="text-base">{t('products.activeCurrencies')}</Label>
            <RadioGroup value={currentDefault} onValueChange={handleSetDefault}>
              {currentCurrencies.map(currencyCode => {
                const currency = getActiveCurrencyDetails(currencyCode);
                if (!currency) return null;

                const isDefault = currencyCode === currentDefault;
                const canRemove = !isDefault && currentCurrencies.length > 1;
                const isUpdating = addCurrencyMutation.isPending || deleteCurrencyMutation.isPending || setPrimaryMutation.isPending;

                return (
                  <div 
                    key={currencyCode}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value={currencyCode} id={currencyCode} />
                      <Label 
                        htmlFor={currencyCode} 
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <span className="font-medium">
                          {currency.code} - {currency.name}
                        </span>
                        {isDefault && (
                          <Badge variant="secondary">{t('common.default')}</Badge>
                        )}
                      </Label>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!canRemove || isUpdating}
                      onClick={() => handleRemoveCurrency(currencyCode)}
                      className="h-8 w-8 p-0"
                    >
                      {isUpdating ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <X className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                );
              })}
            </RadioGroup>
          </div>

          <Button
            variant="outline"
            className="w-full"
            onClick={() => setShowAddCurrency(true)}
            disabled={addCurrencyMutation.isPending || deleteCurrencyMutation.isPending || setPrimaryMutation.isPending}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('products.addCurrency')}
          </Button>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              {t('products.defaultCurrencyInfo')}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Add Currency Dialog */}
      <Dialog open={showAddCurrency} onOpenChange={setShowAddCurrency}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('products.addCurrency')}</DialogTitle>
            <DialogDescription>
              {t('products.selectCurrencyToAdd')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Input
              placeholder={t('products.searchCurrencies')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />
            
            <div className="h-[300px] w-full rounded-md border overflow-y-auto">
              <div className="p-4 space-y-2">
                {filteredCurrencies.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {t('products.noCurrenciesFound')}
                  </p>
                ) : (
                  filteredCurrencies.map(currency => (
                    <button
                      key={currency.code}
                      className="w-full flex items-center justify-between p-3 hover:bg-accent rounded-lg text-left transition-colors"
                      onClick={() => handleAddCurrency(currency.code)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{currency.symbol}</span>
                        <div>
                          <p className="font-medium">{currency.code}</p>
                          <p className="text-sm text-muted-foreground">{currency.name}</p>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCurrency(false)}>
              {t('common.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
